
import React, { useRef, useState } from 'react';
import { useUser } from '@/contexts/UserContext';
import { useProject } from '@/components/ProjectSelector';
import { SectionLayout } from '@/components/section-layout';
import { fetchEntityList } from '@/lib/entity-api';
import { useEntityList } from '@/hooks/useEntityList';
import { useCompanyLookups } from '@/hooks/useCompanyLookups';
import { exportEntityCsv } from '@/lib/csv';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GenericTag } from '@/styles/GenericTag';
import { format } from 'date-fns';
import { Copy, Trash2, Download, Upload } from 'lucide-react';
import { MaterialOrderForm } from './MaterialOrderForm';
import { trackMaterialOrderCreated, trackButtonClick, trackFormSubmission } from '@/lib/posthog-events';
import { getApiUrl } from '@/lib/api-config';
import { fetchMaterialOrderAttachments, fetchMaterialOrderById, fetchMaterialOrderLines, saveMaterialOrderLines } from './store';
import type { MaterialOrderSubmit } from './MaterialOrderForm';
import { useOpenEntityRecord } from '@/hooks/useOpenEntity';
import { lineFromRow, toLinePayload } from '@/features/cost-management/purchase-orders/lib/po-lines';
import { newLineKey } from '@/lib/line-items';




interface MaterialOrderRecord {
  id: string;
  name?: string | null;
  project_id: string;
  supplier: string;
  other_supplier?: string | null;
  vendor_id?: string | null;
  status: string;
  notes: string | null;
  tax: number;
  other_amount: number;
  total_cost: number | null;
  company_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  material_order_id?: string | number | null;
  project?: {
    id: string;
    name: string;
  };
  attachment_count?: number;
  comment_count?: number;
}


interface MaterialOrderAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_url: string;
  file_size: number;
  created_at: string;
  created_by: string;
  created_by_user?: {
    display_name?: string | null;
  };
}

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return format(date, 'MMM d, yyyy');
};

export function MaterialOrdersPage() {
  const { selectedProject } = useProject();
  const { companyId, user } = useUser();
  // The list: ONE server-driven query (project + search + sort + page) -- search, sort and paging run in the database
  // over every material order the user may see; the counters come with each row (hooks/useEntityList.ts).
  const list = useEntityList<any, MaterialOrderRecord>({
    entityType: 'material_list',
    projectId: selectedProject,
    enabled: !!companyId,
    mode: 'pages',
    includeCounts: true,
    defaultSort: { columnKey: 'material_order_id', direction: 'asc', sortKey: 'material_order_id' },
    mapRow: (row) => ({ ...row, attachment_count: row.attachment_count || 0, comment_count: row.comment_count || 0 }),
  });
  const orders = list.items;
  const [selectedOrder, setSelectedOrder] = useState<MaterialOrderRecord | null>(null);
  const { projects } = useCompanyLookups(!!companyId);
  const [isNewOrderOpen, setIsNewOrderOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingOrder, setEditingOrder] = useState<MaterialOrderRecord | null>(null);
  const [orderAttachments, setOrderAttachments] = useState<Record<string, MaterialOrderAttachment[]>>({});
  // the order created by a Save whose items or files then failed: the next Save continues with THAT order instead of creating a second one
  const createdOrderIdRef = useRef<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const formatMaterialOrderId = (order: MaterialOrderRecord): string => {
    if (!order.material_order_id) return '-';
    if (typeof order.material_order_id === 'number') {
      return `MO${order.material_order_id.toString().padStart(6, '0')}`;
    }
    if (typeof order.material_order_id === 'string') {
      // If already in MO000001 format, return as is
      if (order.material_order_id.match(/^MO\d{6}$/)) {
        return order.material_order_id;
      }
      // If it's a number string, format it
      const match = order.material_order_id.match(/\d+/);
      if (match) {
        return `MO${match[0].padStart(6, '0')}`;
      }
      return order.material_order_id;
    }
    return '-';
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(orders.map((order: MaterialOrderRecord) => order.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (orderId: string, checked: boolean) => {
    if (checked) {
      setSelectedRows((prev: string[]) => [...prev, orderId]);
    } else {
      setSelectedRows((prev: string[]) => prev.filter((id: string) => id !== orderId));
    }
  };

  const handleCopySelected = async () => {
    if (selectedRows.length === 0) {
      // Do nothing if nothing is selected - no message
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to copy material orders');
      return;
    }

    try {
      for (const orderId of selectedRows) {
        const order = orders.find((o: MaterialOrderRecord) => o.id === orderId);
        if (!order) continue;

        // The items of this order, to be copied as they are (catalog link, vendor, cost code, price)
        const sourceLines = (await fetchMaterialOrderLines(orderId)).map((row) => ({ ...lineFromRow(row, null), key: newLineKey() }));

        // Create new material order without attachments, comments, or related records
        // Don't include material_order_id - let API auto-generate it
        const newOrderData = {
          name: order.name,
          project_id: order.project_id,
          supplier: order.other_supplier || order.supplier,
          other_supplier: null,
          vendor_id: order.vendor_id ?? null,
          status: 'draft',
          notes: order.notes,
          tax: order.tax || 0,
          other_amount: order.other_amount || 0,
          // Explicitly exclude material_order_id to let API generate it
        };

        const response = await fetch(getApiUrl('api/entities/material_list'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user?.id,
            company_id: companyId,
            ...newOrderData,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to copy material order');
        }

        const result = await response.json();
        const newOrderId = result.data?.id;

        if (newOrderId && sourceLines.length > 0) {
          await saveMaterialOrderLines(newOrderId, toLinePayload(sourceLines));
        }
      }

      list.reload();
      setSelectedRows([]);
    } catch (error: any) {
      console.error('Error copying material orders:', error);
      alert(error.message || 'Failed to copy material orders');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one material order to delete.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} material order(s)? This action cannot be undone.`)) {
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to delete material orders');
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const orderId of selectedRows) {
        try {
          // First delete all items
          const itemsData = await fetchEntityList<any>('material_list_item', { filters: { material_list_id: orderId } });

          if (itemsData && itemsData.length > 0) {
            for (const item of itemsData) {
              const itemResponse = await fetch(getApiUrl(`api/entities/material_list_item/${item.id}`), {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  user_id: user?.id,
                  company_id: companyId,
                }),
              });

              if (!itemResponse.ok) {
                console.error('Error deleting material list item:', item.id);
              }
            }
          }

          // Then delete the material order
          const response = await fetch(getApiUrl(`api/entities/material_list/${orderId}`), {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user?.id,
              company_id: companyId,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error deleting material order:', errorData);
            errorCount++;
            continue;
          }

          successCount++;
        } catch (error) {
          console.error('Error deleting material order:', error);
          errorCount++;
        }
      }

      list.reload();
      setSelectedRows([]);

      if (errorCount > 0) {
        alert(`Deleted ${successCount} material order(s). ${errorCount} failed.`);
      } else {
        alert(`Successfully deleted ${successCount} material order(s).`);
      }
    } catch (error: any) {
      console.error('Error deleting material orders:', error);
      alert(error.message || 'Failed to delete material orders');
    }
  };

  // Export to CSV: the orders selected in the table, or -- with nothing selected -- EVERY material order of the current
  // project + search, read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedRows, 'material_orders', 'material orders');

  const fetchOrderDetails = async (orderId: string) => {
    try {
      // The form loads its own comments (useEntityComments) and its own items; here: the files of THIS order.
      const attachmentsData = await fetchMaterialOrderAttachments(orderId);
      setOrderAttachments((prev) => ({ ...prev, [orderId]: attachmentsData }));
    } catch (error) {
      console.error('Error fetching material order details:', error);
    }
  };

  // Save of a NEW order: the order itself, then its items (one request: validated and all-or-nothing; the server derives total_cost from them + delivery /
  // tax / other), then its files. If the items or the files fail, the order already exists: `createdOrderIdRef` makes the next Save continue with THAT order, so
  // a retry (or a double click) can never create a second one. Errors are re-thrown so MaterialOrderForm shows them and stays open with the user's changes.
  const handleCreateOrder = async (data: MaterialOrderSubmit) => {
    try {
      if (!companyId || !user?.id) {
        throw new Error('No company ID or user ID found');
      }

      if (!data.project_id) {
        throw new Error('Please fill in all required fields: Project');
      }

      let orderId = createdOrderIdRef.current;
      if (!orderId) {
        const response = await fetch(getApiUrl('api/entities/material_list'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.id,
            company_id: companyId,
            name: data.name,
            project_id: data.project_id,
            supplier: data.supplier,
            other_supplier: null,
            vendor_id: data.vendor_id,
            status: data.status,
            notes: data.notes || null,
            tax: data.tax || 0,
            other_amount: data.other_amount || 0,
            created_by: user.id,
            created_at: new Date().toISOString(),
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create material list');
        }

        orderId = (await response.json()).data?.id ?? null;
        createdOrderIdRef.current = orderId;
      }

      if (orderId) {
        if (data.lines && data.lines.length > 0) {
          try {
            await saveMaterialOrderLines(orderId, data.lines);
          } catch (failure: any) {
            throw new Error(`The material order was saved, but its items could not be saved: ${failure?.message || 'unknown error'}`);
          }
        }

        if (data.attachmentsFiles.length > 0) {
          const { uploadMaterialOrderAttachments } = await import('./store');
          await uploadMaterialOrderAttachments(orderId, data.attachmentsFiles, companyId, user.id);
        }
      }

      createdOrderIdRef.current = null;
      list.reload();
      setIsNewOrderOpen(false);
      setError(null);

      // Analytics only: the persisted total_cost is always the backend's own value
      const subtotal = (data.lines || []).reduce(
        (acc: number, line: any) => acc + (Number(line.quantity) || 0) * (Number(line.unit_price) || 0),
        0
      );
      trackMaterialOrderCreated({
        project_id: data.project_id,
        order_id: orderId,
        order_name: data.name,
        supplier: data.supplier,
        status: data.status,
        total_cost: subtotal + (subtotal * (Number(data.tax) || 0)) / 100 + (Number(data.other_amount) || 0),
        item_count: (data.lines || []).length,
        company_id: companyId,
        created_by: user.id
      });

      trackFormSubmission({
        form_type: 'material_order_creation',
        success: true
      });
    } catch (error: any) {
      console.error('Error creating material order:', error);

      trackFormSubmission({
        form_type: 'material_order_creation',
        success: false,
        error_message: error.message || 'Failed to create material order'
      });

      setError(error.message || 'Failed to create material order');
      throw error;
    }
  };

  // Save of an existing order: its own fields, then -- only when the user changed them -- ALL its items in one request (the same all-or-nothing replace;
  // saving the same list twice leaves the same items). Saving the order's fields alone makes the server recompute total_cost (delivery / tax / other).
  // `close: false` is for Create PO: it keeps the form open on the saved order.
  const handleSaveChanges = async (data: MaterialOrderSubmit, options: { close?: boolean } = {}) => {
    try {
      if (!editingOrder) return;

      const response = await fetch(getApiUrl(`api/entities/material_list/${editingOrder.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user?.id,
          company_id: companyId,
          name: data.name,
          project_id: data.project_id,
          supplier: data.supplier,
          other_supplier: null,
          vendor_id: data.vendor_id,
          status: data.status,
          notes: data.notes,
          tax: data.tax || 0,
          other_amount: data.other_amount || 0,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update material list');
      }

      if (data.lines) {
        try {
          await saveMaterialOrderLines(editingOrder.id, data.lines);
        } catch (failure: any) {
          throw new Error(`The material order was saved, but its items could not be saved: ${failure?.message || 'unknown error'}`);
        }
      }

      list.reload();
      await fetchOrderDetails(editingOrder.id);
      setError(null);
      if (options.close !== false) {
        setEditingOrder(null);
        setHasUnsavedChanges(false);
        setIsNewOrderOpen(false);
      }
    } catch (error: any) {
      console.error('Error updating material order:', error);
      setError(error.message || 'Failed to update material order');
      throw error;
    }
  };

  // Opens an order for editing (a row click, or a link from another document: a purchase order made from it)
  const openOrder = (order: MaterialOrderRecord) => {
    createdOrderIdRef.current = null;
    setEditingOrder(order);
    fetchOrderDetails(order.id);
    setIsNewOrderOpen(true);
  };
  const handleRowClick = openOrder;
  useOpenEntityRecord('material_order', fetchMaterialOrderById as (id: string) => Promise<MaterialOrderRecord | undefined>, openOrder);

  // Define table columns (placed after all handler functions to avoid hoisting issues)
  const columns: GenericTableColumn<MaterialOrderRecord>[] = [
    {
      key: 'material_order_id',
      header: 'Mat Order ID',
      width: '6.5rem',
      sortable: true,
      sortKey: 'material_order_id',
      render: (row) => <span className="font-medium">{formatMaterialOrderId(row)}</span>,
    },
    {
      key: 'name',
      header: 'Title',
      width: '12.5rem',
      sortable: true,
      sortKey: 'name',
      render: (row) => <span className="font-medium">{row.name || 'Untitled Order'}</span>,
    },
    {
      key: 'project_id',
      header: 'Project',
      width: '8.5rem',
      sortable: true,
      sortKey: 'project',
      render: (row) => row.project?.name || row.project_id,
    },
    {
      key: 'status',
      header: 'Status',
      width: '6.5rem',
      sortable: true,
      sortKey: 'status',
      render: (row) => {
        const mapStatusToTag = (status: string): 'draft' | 'submitted' | 'approved' | 'ordered' | 'received' | 'cancelled' => {
          switch (status.toLowerCase()) {
            case 'draft':
              return 'draft';
            case 'submitted':
              return 'submitted';
            case 'approved':
              return 'approved';
            case 'ordered':
              return 'ordered';
            case 'received':
              return 'received';
            case 'cancelled':
              return 'cancelled';
            default:
              return 'draft';
          }
        };
        return <GenericTag type="status" value={mapStatusToTag(row.status)} />;
      },
    },
    {
      key: 'supplier',
      header: 'Supplier',
      width: '6.5rem',
      sortable: true,
      sortKey: 'supplier',
      render: (row) => row.other_supplier || row.supplier,
    },
    {
      key: 'total_cost',
      header: 'Total',
      width: '6.5rem',
      sortable: true,
      sortKey: 'total_cost',
      render: (row) => row.total_cost ? `$${row.total_cost.toFixed(2)}` : '-',
    },
    {
      key: 'attachment_count',
      header: 'Attachments',
      width: '6.25rem',
      align: 'center' as const,
      render: (row) => row.attachment_count || 0,
    },
    {
      key: 'comment_count',
      header: 'Comments',
      width: '6.25rem',
      align: 'center' as const,
      render: (row) => row.comment_count || 0,
    },
  ];

  const menuItems = [
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopySelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => console.log('Import clicked'),
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
  ];

  // Don't block page - let table show with skeleton rows

  return (
    <SectionLayout title="Material Orders">
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <NewItemButton
              label="New"
              onClick={() => {
                trackButtonClick('new_material_order_button', { context: 'project_management' });
                createdOrderIdRef.current = null;
                setEditingOrder(null);
                setOrderAttachments({});
                setIsNewOrderOpen(true);
              }}
            />
            <SearchButton
              onSearch={list.setSearchInput}
              placeholder="Search"
            />
            <ThreeDotMenuButton items={menuItems} />
          </div>
        </div>

        {/* Generic Table */}
        <GenericTable
          columns={columns}
          {...list.tableProps}
          rowKey={(row) => row.id}
          scrollContainerMaxWidth="calc(100vw - 19.2rem)"
          minTableWidth={1200}
          onRowClick={handleRowClick}
          selectable={true}
          selectedKeys={selectedRows}
          onSelectionChange={setSelectedRows}
          emptyState="No material orders found"
        />

        {isNewOrderOpen && (
              <MaterialOrderForm
              mode={editingOrder ? 'edit' : 'create'}
              initialData={editingOrder || undefined}
              projects={projects}
                onDirtyChange={(dirty: boolean) => setHasUnsavedChanges(dirty)}
              onSubmit={editingOrder ? handleSaveChanges : handleCreateOrder}
            onCancel={() => {
              // GenericForm handles unsaved changes warnings internally
              setIsNewOrderOpen(false);
              setHasUnsavedChanges(false);
            }}
              attachments={editingOrder ? orderAttachments[editingOrder.id] || [] : []}
              onRelatedDataChanged={list.reload}
            />
        )}
      </div>
    </SectionLayout>
  );
}

